import {
  PDF_AMOUNT_COLUMN_WIDTH,
  PDF_LINE_INDENT,
  PDF_NO_BORDER_LAYOUT,
} from "./invoicePdfLayout";

function isFlatFeeCategory(category) {
  return String(category ?? "").trim().toLowerCase() === "flat fee";
}

function isMonthlyUnitOfMeasure(basedOn) {
  const normalized = String(basedOn ?? "").trim().toLowerCase();
  return normalized === "month" || normalized === "monthly" || normalized === "montly";
}

function usesUnitQuantityOne(category, basedOn) {
  return isFlatFeeCategory(category) || isMonthlyUnitOfMeasure(basedOn);
}

function lineHasQuantityOverride(line) {
  return line?.overrideQuantity != null && Number.isFinite(Number(line.overrideQuantity));
}

function lineEffectiveQuantity(line) {
  if (lineHasQuantityOverride(line)) {
    return Number(line.overrideQuantity);
  }
  const qty = line?.quantity != null ? Number(line.quantity) : Number.NaN;
  return Number.isFinite(qty) ? qty : null;
}

export function isLineSuppressedForPdf(line) {
  if (!line) {
    return false;
  }
  if (line.suppressed === true) {
    return true;
  }
  if (typeof line.suppressed === "string") {
    return line.suppressed.trim().toUpperCase() === "Y";
  }
  return false;
}

export function computeLineDisplayAmount(line, profileDetail) {
  if (isLineSuppressedForPdf(line)) {
    return 0;
  }

  let base = 0;
  if (line.overrideFlag) {
    const overrideValue =
      line.overrideAmount != null ? Number(line.overrideAmount) : Number(line.amount);
    base = Number.isFinite(overrideValue) ? overrideValue : 0;
  } else {
    const effectiveQty = lineEffectiveQuantity(line);
    const rate = line.rate != null ? Number(line.rate) : Number.NaN;
    if (lineHasQuantityOverride(line) && Number.isFinite(effectiveQty) && Number.isFinite(rate)) {
      base = effectiveQty * rate;
    } else {
      const basedOn = String(profileDetail?.basedOn ?? "").trim();
      if (profileDetail && isMonthlyUnitOfMeasure(basedOn) && !lineHasQuantityOverride(line)) {
        if (Number.isFinite(rate)) {
          base = rate;
        }
      } else {
        const numeric = Number(line.amount);
        base = Number.isFinite(numeric) ? numeric : 0;
      }
    }
  }

  const rollup = Number(line.pdfRollupAmount);
  return base + (Number.isFinite(rollup) ? rollup : 0);
}

export function formatInvoicePdfCurrency(value) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) {
    return "$0.00";
  }
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(numeric);
}

function formatQuantity(value) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) {
    return "";
  }
  return new Intl.NumberFormat("en-US", {
    maximumFractionDigits: 2,
  }).format(numeric);
}

function formatUnitLabel(basedOn) {
  const unit = String(basedOn ?? "").trim();
  return unit || "unit";
}

export function buildPdfFlatFeeRow(amount, description, indent = PDF_LINE_INDENT) {
  const amountLabel = formatInvoicePdfCurrency(amount);
  const detail = String(description ?? "").trim();
  return {
    text: detail ? `${amountLabel}  ${detail}` : amountLabel,
    margin: [indent, 2, 0, 2],
  };
}

export function formatInvoicePdfLineEntry(line, profileDetail, indent = PDF_LINE_INDENT) {
  const description = String(line.description ?? "").trim();
  const amount = computeLineDisplayAmount(line, profileDetail);
  const category = String(profileDetail?.category ?? "").trim();
  const basedOn = String(profileDetail?.basedOn ?? "").trim();
  const rate = line.rate != null ? Number(line.rate) : Number.NaN;
  const effectiveQty = lineEffectiveQuantity(line);

  if (
    Number.isFinite(effectiveQty) &&
    effectiveQty !== 0 &&
    Number.isFinite(rate) &&
    !usesUnitQuantityOne(category, basedOn)
  ) {
    const unit = formatUnitLabel(basedOn);
    const qtyLabel = formatQuantity(effectiveQty);
    const rateLabel = formatInvoicePdfCurrency(rate);
    return buildPdfAmountRow({
      leftText: `${qtyLabel} ${unit} @ ${rateLabel} /${unit}`,
      amount,
      indent,
    });
  }

  if (usesUnitQuantityOne(category, basedOn)) {
    return buildPdfFlatFeeRow(amount, description, indent);
  }

  if (description) {
    return buildPdfAmountRow({
      leftText: description,
      amount,
      indent,
    });
  }

  return buildPdfAmountRow({
    leftText: "",
    amount,
    indent,
  });
}

export function buildPdfCategoryHeaderRow(label, amount) {
  return buildPdfAmountRow({
    leftText: label,
    amount,
    indent: 0,
    leftBold: true,
  });
}

export function buildPdfAmountRow({ leftText, amount, indent = 0, leftBold = false }) {
  return {
    table: {
      widths: ["*", PDF_AMOUNT_COLUMN_WIDTH],
      body: [
        [
          {
            text: leftText,
            bold: leftBold,
            margin: [indent, 0, 4, 0],
          },
          {
            text: formatInvoicePdfCurrency(amount),
            alignment: "right",
            noWrap: true,
          },
        ],
      ],
    },
    layout: PDF_NO_BORDER_LAYOUT,
    margin: [0, 2, 0, 2],
  };
}

export { PDF_LINE_INDENT, PDF_AMOUNT_COLUMN_WIDTH };
